import { Controller } from "@hotwired/stimulus"

/**
 * Reusable table sorting controller for Stimulus
 *
 * Usage:
 *   <table data-controller="table-sort"
 *          data-table-sort-default-column-value="2"
 *          data-table-sort-default-direction-value="desc">
 *     <thead>
 *       <tr>
 *         <th data-action="click->table-sort#sort"
 *             data-table-sort-column-param="0"
 *             data-table-sort-type-param="string">Name</th>
 *         <th data-action="click->table-sort#sort"
 *             data-table-sort-column-param="1"
 *             data-table-sort-type-param="number">Value</th>
 *       </tr>
 *     </thead>
 *     <tbody>...</tbody>
 *   </table>
 */
export default class extends Controller {
  static values = {
    defaultColumn: { type: Number, default: -1 },
    defaultDirection: { type: String, default: "desc" }
  }

  connect() {
    // Initialize internal state
    this._currentColumn = -1
    this._currentDirection = "asc"

    this.applyDefaultSort()
  }

  // Expose current state for testing
  get currentColumn() { return this._currentColumn }
  get currentDirection() { return this._currentDirection }

  /**
   * Handle click on sortable header
   */
  sort(event) {
    const header = event.currentTarget
    const column = parseInt(header.dataset.tableSortColumnParam, 10)
    const type = header.dataset.tableSortTypeParam || "string"
    const direction = this.determineDirection(column, type)

    this.sortByColumn(column, type, direction, header)
  }

  /**
   * Handle keyboard activation (Enter/Space) on sortable header
   */
  sortOnKey(event) {
    if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
      event.preventDefault()
      this.sort(event)
    }
  }

  /**
   * Apply default sort on page load if configured
   */
  applyDefaultSort() {
    if (this.defaultColumnValue < 0) return

    const header = this.findHeaderByColumn(this.defaultColumnValue)
    if (!header) return

    const type = header.dataset.tableSortTypeParam || "string"
    this.sortByColumn(this.defaultColumnValue, type, this.defaultDirectionValue, header)
  }

  /**
   * Determine sort direction - toggle if same column, otherwise use defaults
   */
  determineDirection(column, type) {
    if (this._currentColumn === column) {
      return this._currentDirection === "asc" ? "desc" : "asc"
    }
    // Default: descending for numbers (show highest first), ascending for text
    return type === "number" ? "desc" : "asc"
  }

  /**
   * Sort table rows by specified column
   */
  sortByColumn(column, type, direction, header) {
    const tbody = this.element.querySelector("tbody")
    if (!tbody) return

    const rows = Array.from(tbody.querySelectorAll("tr"))
    const sortedRows = this.sortRows(rows, column, type, direction)

    // Reorder DOM efficiently
    sortedRows.forEach(row => tbody.appendChild(row))

    // Update internal state
    this._currentColumn = column
    this._currentDirection = direction

    // Update visual indicators
    this.updateHeaderIndicators(header, direction)
  }

  /**
   * Sort rows array by column value
   */
  sortRows(rows, column, type, direction) {
    return rows.sort((rowA, rowB) => {
      const valueA = this.getCellValue(rowA, column, type)
      const valueB = this.getCellValue(rowB, column, type)

      return this.compareValues(valueA, valueB, type, direction)
    })
  }

  /**
   * Extract and parse cell value based on type
   * Supports data-sort-value attribute for custom sort values (e.g., timestamps for dates)
   */
  getCellValue(row, column, type) {
    const cell = row.cells[column]

    // Check for explicit sort value attribute (useful for dates stored as timestamps)
    const sortValue = cell?.dataset?.sortValue
    if (sortValue !== undefined) {
      return type === "number" ? this.parseNumericValue(sortValue) : sortValue
    }

    const text = cell?.textContent?.trim() || ""

    if (type === "number") {
      return this.parseNumericValue(text)
    }
    return text
  }

  /**
   * Parse numeric value from text (handles percentages like "+5.2%", "-3.1%", "-" and currency "$80.77")
   */
  parseNumericValue(text) {
    const cleaned = text.replace(/[%,+$]/g, "").trim()
    if (cleaned === "-" || cleaned === "") return NaN
    return parseFloat(cleaned)
  }

  /**
   * Compare two values for sorting
   */
  compareValues(valueA, valueB, type, direction) {
    if (type === "number") {
      // Push NaN values to the end regardless of direction
      if (isNaN(valueA) && isNaN(valueB)) return 0
      if (isNaN(valueA)) return 1
      if (isNaN(valueB)) return -1

      return direction === "asc" ? valueA - valueB : valueB - valueA
    }

    // String comparison
    const comparison = valueA.localeCompare(valueB)
    return direction === "asc" ? comparison : -comparison
  }

  /**
   * Find header element by column index
   */
  findHeaderByColumn(column) {
    return this.element.querySelector(`th[data-table-sort-column-param="${column}"]`)
  }

  /**
   * Update header indicators to show current sort state
   */
  updateHeaderIndicators(activeHeader, direction) {
    // Clear all existing indicators
    this.element.querySelectorAll("thead th").forEach(th => {
      th.classList.remove("sort-asc", "sort-desc")
      if (th.hasAttribute("aria-sort")) th.setAttribute("aria-sort", "none")
      const indicator = th.querySelector(".sort-indicator")
      if (indicator) indicator.remove()
    })

    // Add indicator to active header
    activeHeader.classList.add(`sort-${direction}`)
    activeHeader.setAttribute("aria-sort", direction === "asc" ? "ascending" : "descending")

    const indicator = document.createElement("span")
    indicator.className = "sort-indicator ms-1"
    indicator.textContent = direction === "asc" ? "↑" : "↓"
    activeHeader.appendChild(indicator)
  }
}
